import React, {useState} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import _ from 'lodash';

import sy from '~/styles';
import {
  Text,
  Pressable,
  Quantity,
  ArticleText,
  SlaArticleText,
  ActionButton,
  WarrantyIcon,
  ReplacementIcon,
  ReplacementText,
  Grouping,
  DurationText,
  DurationIcon,
} from '~/components/controls';
import {Row as GroupingRow} from '~/components/controls/Grouping';
import Actionsheet, {
  Title,
  Content,
  Actions,
} from '~/components/controls/Actionsheet';
import {DiscountDialog, MarkupDialog} from '~/components/shared/Dialogs';
import Format from '~/lib/format';
import withDimensions from '~/components/hoc/with-dimensions';
import {isRobTaskApproved, isRobTaskDenied, LOCATION_TEXT} from '~/types/rob';

import DiscountIcon from '~/images/md-icons/discount/materialsymbolsoutlined/24px.svg';
import SellIcon from '~/images/md-icons/sell/materialsymbolsoutlined/24px.svg';
import FactoryOutlinedIcon from '~/images/md-icons/factory/materialsymbolsoutlined/24px.svg';
import EditOutlinedIcon from '~/images/md-icons/edit/materialsymbolsoutlined/24px.svg';
import HandshakeIcon from '~/images/md-icons/handshake/materialicons/24px.svg';
import ThumbDownIcon from '~/images/md-icons/thumb_down/materialicons/24px.svg';
import ThumbUpIcon from '~/images/md-icons/thumb_up/materialicons/24px.svg';

const Activity = ({rob_code, description}) => (
  <GroupingRow
    style={[sy['gap-2']]}
    contentStyle={[sy['flex-1']]}
    title={<Text style={sy.mediumPlus}>Activiteit</Text>}>
    <Text style={[sy.truncate, sy['text-right']]}>
      {rob_code}&nbsp;&middot;&nbsp;
      {description}
    </Text>
  </GroupingRow>
);

const Operation = ({operation, reason}) => {
  if (!operation) {
    return null;
  }

  return (
    <GroupingRow
      style={[sy['gap-2']]}
      contentStyle={[sy['flex-1']]}
      title={<Text style={sy.mediumPlus}>Bewerking</Text>}>
      <Text style={[sy.truncate, sy['text-right']]}>
        {operation.description}
        {reason ? <>&nbsp;({reason.description})</> : <></>}
      </Text>
    </GroupingRow>
  );
};

const Location = ({location}) => {
  if (!location) {
    return null;
  }

  return (
    <GroupingRow
      style={[sy['gap-2']]}
      contentStyle={[sy['flex-1']]}
      title={<Text style={sy.mediumPlus}>Locatie</Text>}>
      <Text style={[sy.truncate, sy['text-right']]}>
        {location
          .map((item) => LOCATION_TEXT[item])
          .filter(Boolean)
          .join(' • ')}
      </Text>
    </GroupingRow>
  );
};

const Part = ({
  lessor,
  task_parts,
  onShowDiscountDialog,
  onShowMarkupDialog,
}) => {
  const parts = task_parts?.reduce((acc, item) => {
    acc[item.attributes.itemMpId] = {
      itemMpId: item.attributes.itemMpId,
      ..._.pickBy(item),
    };

    return acc;
  }, {});

  return (
    <>
      {Object.values(parts ?? {}).map((item, index) => {
        const sla_part = item.is_sla_part;
        const is_option_part = !!item.attributes?.option_id;

        const quantity = item.quantity ?? 0;
        const description = item.description;
        const price =
          sla_part && !is_option_part
            ? (_.isNumber(item.price) ? item.price : sla_part.price) * quantity
            : _.isNumber(item.price) && _.isNumber(item.quantity)
            ? item.price * item.quantity
            : null;

        return (
          <React.Fragment key={item.id}>
            <View style={[sy['flex-row']]}>
              <View style={[sy['p-4']]}>
                <Quantity quantity={quantity} />
              </View>
              <View
                style={[
                  sy['flex-row'],
                  sy['flex-1'],
                  sy['justify-between'],
                  sy['py-4'],
                  sy['pr-4'],
                  sy['gap-4'],
                ]}>
                <View style={[sy['flex-1']]}>
                  <Text>{description}</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    {sla_part ? (
                      <SlaArticleText
                        lessor={lessor}
                        description={sla_part.description}
                        attributes={item.attributes}
                      />
                    ) : (
                      <ArticleText source={item.source} number={item.number} />
                    )}
                  </Text>
                </View>
                <View style={[sy['self-start'], sy['items-end']]}>
                  <Pressable
                    disabled={!item.markup && !item.discount}
                    onPress={() => {
                      if (item.discount > 0) {
                        onShowDiscountDialog({
                          discount: item.discount,
                          amount: price,
                        });
                      } else if (item.markup > 0) {
                        onShowMarkupDialog({
                          markup: item.markup,
                          amount: price,
                        });
                      }
                    }}>
                    <Text
                      style={[
                        sy.smallRegular,
                        (item.discount > 0 || item.markup > 0) && sy.underline,
                      ]}>
                      {_.isNumber(price) ? (
                        Format.price(
                          Math.max(
                            price + (item.markup ?? 0) - (item.discount ?? 0),
                            0,
                          ),
                        )
                      ) : (
                        <></>
                      )}
                    </Text>
                  </Pressable>
                  {item.discount > 0 && (
                    <DiscountIcon fill="#299976" width={18} height={18} />
                  )}
                  {item.markup > 0 && (
                    <SellIcon fill="#FF6C00" width={18} height={18} />
                  )}
                </View>
              </View>
            </View>
            {index < Object.values(parts).length - 1 && (
              <Divider style={[{backgroundColor: '#f2f2f2'}, sy['ml-18']]} />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const HEADER_HEIGHT_IN_PX = 72;
const FOOTER_HEIGHT_IN_PX = 52;

const Task = ({
  dimensions,
  onDismiss,
  is_cancelled,
  service_request_id,
  lessor,
  tasks,
  parts,
  component,
  is_replacement,
}) => {
  const main_task = tasks?.find((t) => t.is_main_task);

  const [showDiscountDialog, setShowDiscountDialog] = useState(null);
  const [showMarkupDialog, setShowMarkupDialog] = useState(null);

  return (
    <>
      <Actionsheet visible={true} onDismiss={onDismiss}>
        <Title>
          <View
            style={[
              sy['flex-row'],
              sy['justify-between'],
              sy['items-center'],
              sy['flex-1'],
              {
                width: '100%',
              },
            ]}>
            <Text style={[sy.largePlus, sy.truncate, {lineHeight: 30}]}>
              {component.rob_component.rob.description}
            </Text>
            {isRobTaskDenied(component.rob_component.status.code) ? (
              <ThumbDownIcon fill="#972727" width={24} height={24} />
            ) : isRobTaskApproved(component.rob_component.status.code) ? (
              <ThumbUpIcon fill="#299976" width={24} height={24} />
            ) : null}
          </View>
        </Title>

        <Content style={{paddingBottom: 0, paddingHorizontal: 0}}>
          <ScrollView
            contentContainerStyle={{
              maxHeight:
                dimensions.window.height -
                HEADER_HEIGHT_IN_PX -
                FOOTER_HEIGHT_IN_PX,
            }}>
            <Grouping header="Arbeid" initial={{expand: true}}>
              <View style={[sy['p-4'], sy['gap-4']]}>
                {tasks
                  .sort((a, b) => {
                    if (a.is_main_task && !b.is_main_task) return -1;
                    if (!a.is_main_task && b.is_main_task) return 1;
                    return (a.work_text ?? a.description).localeCompare(
                      b.work_text ?? b.description,
                    );
                  })
                  .map((task, index) => (
                    <React.Fragment key={task.id}>
                      {index > 0 && <Divider style={sy['bg-gray95']} />}
                      <View
                        style={[
                          sy['flex-row'],
                          sy['justify-between'],
                          sy['gap-4'],
                          sy['overflow-hidden'],
                        ]}>
                        <View
                          style={[sy['flex-row'], sy['flex-1'], sy['gap-4']]}>
                          <View style={[sy['justify-center']]}>
                            {task.amount > 0 ? (
                              <HandshakeIcon fill="#4a4a49" />
                            ) : task.reference ? (
                              <FactoryOutlinedIcon fill="#4A4A49" />
                            ) : is_replacement ? (
                              <ReplacementIcon type="transport" />
                            ) : (
                              <EditOutlinedIcon />
                            )}
                          </View>
                          <View style={[sy['flex-1']]}>
                            <View
                              style={[
                                sy['flex-row'],
                                sy['justify-between'],
                                sy['gap-4'],
                              ]}>
                              <Text>
                                {task.amount > 0 ? (
                                  <>Vaste prijs</>
                                ) : is_replacement ? (
                                  <ReplacementText type="transport" />
                                ) : task.work_text ? (
                                  task.work_text
                                ) : (
                                  <>Handmatige invoer</>
                                )}
                              </Text>
                              <View style={[sy['flex-row'], sy['gap-2']]}>
                                {task.markup > 0 ? (
                                  <SellIcon
                                    fill="#FF6C00"
                                    width={18}
                                    height={18}
                                  />
                                ) : task.discount > 0 ? (
                                  <DiscountIcon
                                    fill="#299976"
                                    width={18}
                                    height={18}
                                  />
                                ) : (
                                  <></>
                                )}
                                <Pressable
                                  disabled={
                                    !(task.markup > 0 || task.discount > 0)
                                  }
                                  onPress={() => {
                                    if (task.discount) {
                                      setShowDiscountDialog({
                                        discount: task.discount,
                                        amount: _.isNumber(task.amount)
                                          ? task.amount
                                          : task.duration * task.hourly_rate,
                                      });
                                    } else if (task.markup > 0) {
                                      setShowMarkupDialog({
                                        markup: task.markup,
                                        amount: _.isNumber(task.amount)
                                          ? task.amount
                                          : task.duration * task.hourly_rate,
                                      });
                                    }
                                  }}>
                                  <Text
                                    style={[
                                      sy.smallRegular,
                                      (task.markup > 0 || task.discount > 0) &&
                                        sy.underline,
                                    ]}>
                                    {is_cancelled
                                      ? ''
                                      : Format.price(
                                          (_.isNumber(task.amount)
                                            ? task.amount
                                            : task.duration *
                                              task.hourly_rate) +
                                            task.markup -
                                            task.discount,
                                        )}
                                  </Text>
                                </Pressable>
                              </View>
                            </View>
                            <View
                              style={[sy['flex-row'], sy['justify-between']]}>
                              <Text style={[sy.small, sy['text-lightgray']]}>
                                {task.amount > 0 ? (
                                  <>SLA&nbsp;&middot;&nbsp;{lessor}</>
                                ) : task.work_text ? (
                                  <>{task.work_source ?? 'TecRMI'}</>
                                ) : (
                                  <>Invoer</>
                                )}
                              </Text>
                              <View style={[sy['flex-row'], sy['gap-2']]}>
                                <DurationIcon
                                  duration={task.duration}
                                  original_duration={task.original_duration}
                                  original_work_time={task.original_work_time}
                                  calculated_work_time={
                                    task.calculated_work_time
                                  }
                                />
                                <DurationText
                                  duration={task.duration}
                                  original_duration={task.original_duration}
                                  original_work_time={task.original_work_time}
                                  calculated_work_time={
                                    task.calculated_work_time
                                  }
                                />
                              </View>
                            </View>
                          </View>
                        </View>
                      </View>
                    </React.Fragment>
                  ))}
              </View>
            </Grouping>
            {parts?.length > 0 && (
              <>
                <Divider style={[{backgroundColor: '#f2f2f2'}]} />
                <View
                  style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                  <Text style={sy.mediumPlus}>Artikel(en)</Text>
                </View>
                <Divider style={[{backgroundColor: '#f2f2f2'}]} />
                <View>
                  <Part
                    service_request_id={service_request_id}
                    lessor={lessor}
                    task_parts={parts}
                    onShowDiscountDialog={setShowDiscountDialog}
                    onShowMarkupDialog={setShowMarkupDialog}
                  />
                </View>
                <Divider style={[{backgroundColor: '#f2f2f2'}, sy['ml-18']]} />
              </>
            )}
            <Grouping header="ROB-Net" initial={{expand: true}}>
              <Activity
                rob_code={component?.rob_component?.rob.code}
                description={component?.rob_component?.rob.description}
              />

              <Operation
                operation={component?.rob_component?.operation}
                reason={component?.rob_component?.reason}
              />

              <Location location={component?.rob_component?.location} />
            </Grouping>
          </ScrollView>
        </Content>
        <Divider style={sy['bg-gray88']} />
        <Actions style={[sy['justify-between']]}>
          <View style={[sy['flex-row'], sy['items-center']]}>
            <Text style={[sy.mediumBold, sy['pl-2'], sy['pr-4']]}>
              {Format.price(component.rob_component.price)}
            </Text>
            <View style={[sy['flex-row'], sy['items-center']]}>
              {(main_task.warranty || main_task.is_damage) && (
                <WarrantyIcon
                  description={component.rob_component.rob.description}
                  value={{
                    warranty: main_task.warranty,
                    is_damage: main_task.is_damage,
                    leniency_amount: main_task.leniency_amount,
                  }}
                />
              )}
            </View>
          </View>
          <View style={[sy['flex-row'], sy['gap-2']]}>
            <ActionButton onPress={onDismiss}>OK</ActionButton>
          </View>
        </Actions>
      </Actionsheet>
      {showDiscountDialog && (
        <DiscountDialog
          value={showDiscountDialog.discount}
          amount={showDiscountDialog.amount}
          onDismiss={() => setShowDiscountDialog(null)}
        />
      )}
      {showMarkupDialog && (
        <MarkupDialog
          value={showMarkupDialog.markup}
          amount={showMarkupDialog.amount}
          onDismiss={() => setShowMarkupDialog(null)}
        />
      )}
    </>
  );
};

export default withDimensions(Task);
